import { z } from 'zod';
import { Currency, SourceSystem } from '../enums.js';
import { DecimalString, IsoDate, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';
import { UomCode } from './uom-code.js';

/**
 * S17 — Sales Order (Quotation → Confirmed → In_Progress → Delivered → Invoiced → Closed).
 * Сущность между Counterparty и Invoice. Один SO ↔ много Projects (Odoo-style 1:N) +
 * много Invoices (header- или line-level linkage).
 *
 * SSOT для frontend G2 (list) / G3 (detail) / G16 (form).
 */

export const SalesOrderStatus = z.enum([
  'draft',
  'quotation',
  'confirmed',
  'in_progress',
  'delivered',
  'invoiced',
  'closed',
  'cancelled',
]);
export type SalesOrderStatus = z.infer<typeof SalesOrderStatus>;

/** Shared label map (паттерн WORK_ORDER_STATUS_LABEL). Используется в FE G2.2 / G3.4. */
export const SALES_ORDER_STATUS_LABEL: Record<SalesOrderStatus, string> = {
  draft: 'Черновик',
  quotation: 'Котировка',
  confirmed: 'Подтверждён',
  in_progress: 'В работе',
  delivered: 'Отгружен',
  invoiced: 'Выставлен счёт',
  closed: 'Закрыт',
  cancelled: 'Отменён',
};

export const SalesOrderLine = z
  .object({
    id: UuidString,
    sales_order_id: UuidString,
    position: z.number().int().nonnegative(),
    item_id: UuidString,
    /** JOIN enrichment — backend populate. */
    item_name: z.string().nullable().optional(),
    item_sku: z.string().nullable().optional(),
    item_uom: z.string().nullable().optional(),
    qty: DecimalString,
    uom_code: UomCode,
    unit_price: MoneyAmount,
    line_total: MoneyAmount,
    /** Computed via P10.3 — sum stock_entries.qty linked к этой SO line. */
    delivered_qty: DecimalString.default('0'),
    /** Computed via P10.3 — sum invoice_lines.qty с этой sales_order_line link. */
    invoiced_qty: DecimalString.default('0'),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .passthrough();
export type SalesOrderLine = z.infer<typeof SalesOrderLine>;

export const SalesOrder = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    /** Server-generated `SO-YYYY-####` (P2.3 voucher generator). */
    code: z.string().min(1),
    counterparty_id: UuidString,
    /** JOIN enrichment. */
    counterparty_name: z.string().nullable().optional(),
    status: SalesOrderStatus.default('draft'),
    currency: Currency,
    order_date: IsoDate,
    delivery_expected_date: IsoDate.nullable(),
    delivery_actual_date: IsoDate.nullable(),
    contract_number: z.string().nullable(),
    contract_date: IsoDate.nullable(),
    /** Lines — eager-loaded в GET /:id, omitted в list endpoint. */
    lines: z.array(SalesOrderLine).optional(),
    total_qty: DecimalString.default('0'),
    total_amount: MoneyAmount,
    /** Computed via P10.3 — sum invoices.total с этой SO. */
    total_invoiced_amount: MoneyAmount.optional(),
    notes: z.string().nullable(),
    is_active: z.boolean().default(true),
    source_system: SourceSystem.default('staiter'),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .passthrough();
export type SalesOrder = z.infer<typeof SalesOrder>;

/** P2.3 — input для POST/PUT (header только; lines управляются отдельными endpoint'ами). */
export const SalesOrderUpsertInput = z.object({
  counterparty_id: UuidString,
  currency: Currency,
  order_date: IsoDate,
  delivery_expected_date: IsoDate.nullable().optional(),
  contract_number: z.string().nullable().optional(),
  contract_date: IsoDate.nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type SalesOrderUpsertInput = z.infer<typeof SalesOrderUpsertInput>;

/** P2.5 — input для line CRUD. */
export const SalesOrderLineUpsertInput = z.object({
  item_id: UuidString,
  qty: DecimalString,
  uom_code: UomCode,
  unit_price: MoneyAmount,
  position: z.number().int().nonnegative().optional(),
});
export type SalesOrderLineUpsertInput = z.infer<typeof SalesOrderLineUpsertInput>;

/** P2.6 — transition signal. */
export const SalesOrderTransitionInput = z.object({
  target_status: SalesOrderStatus,
  reason: z.string().nullable().optional(),
  idempotency_key: z.string().min(1).max(128).optional(),
});
export type SalesOrderTransitionInput = z.infer<typeof SalesOrderTransitionInput>;

/** P2.6 — transition matrix. Любое target из этого списка — manual PM action. */
export const SO_TRANSITION_MATRIX: Record<SalesOrderStatus, readonly SalesOrderStatus[]> = {
  draft: ['quotation', 'cancelled'],
  quotation: ['confirmed', 'cancelled', 'draft'],
  confirmed: ['in_progress', 'cancelled'],
  in_progress: ['delivered', 'cancelled'],
  delivered: ['invoiced', 'cancelled'],
  invoiced: ['closed'],
  closed: [],
  cancelled: [],
} as const;
